import React from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  FaChalkboardTeacher,
  FaUserGraduate,
  FaBookOpen,
  FaChartLine,
  FaComments,
} from "react-icons/fa";
import { RiGitRepositoryPrivateFill } from "react-icons/ri";
import "./Home.css";

export default function Home() {
  const { authData } = useAuth();
  const { t } = useTranslation();

  return (
    <>
      {/* Hero Section */}
      <Container
        fluid
        className="bg-primary text-white text-center py-5 hero-section"
      >
        <Row className="justify-content-center">
          <Col md={8}>
            <h1 className="display-4 fw-bold mb-3">
              {t("welcome_to_profd", "Welcome to ProfD!")}
            </h1>
            <p className="lead mb-4">
              {t(
                "platform_description",
                "The ultimate platform for creating, managing, and joining interactive online courses. Empowering educators and inspiring learners."
              )}
            </p>
            {!authData.isLoggedIn ? (
              <>
                <Link to="/signup">
                  <Button variant="light" size="lg" className="me-2">
                    {t("get_started", "Get Started")}
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button variant="outline-light" size="lg" className="ms-2">
                    {t("browse_catalog", "Browse Catalog")}
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/programs">
                <Button variant="light" size="lg">
                  {t("explore_courses", "Explore Courses")}
                </Button>
              </Link>
            )}
          </Col>
        </Row>
      </Container>

      {/* Features Section */}
      <Container className="py-5">
        <h2 className="text-center mb-5 fw-bold">
          {t("key_features", "Key Features")}
        </h2>
        <Row xs={1} md={2} lg={3} className="g-4">
          <Col>
            <Card className="h-100 text-center shadow-sm feature-card">
              <Card.Body>
                <FaChalkboardTeacher size={50} className="text-primary mb-3" />
                <Card.Title className="fw-bold">
                  {t("feature_course_creation", "Easy Course Creation")}
                </Card.Title>
                <Card.Text>
                  {t(
                    "feature_course_creation_desc",
                    "Build engaging courses with rich text, quizzes, PDFs, and interactive challenges using our intuitive editor."
                  )}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card className="h-100 text-center shadow-sm feature-card">
              <Card.Body>
                <FaUserGraduate size={50} className="text-success mb-3" />
                <Card.Title className="fw-bold">
                  {t("feature_student_enrollment", "Seamless Enrollment")}
                </Card.Title>
                <Card.Text>
                  {t(
                    "feature_student_enrollment_desc",
                    "Students can easily join courses using unique codes or by requesting access from instructors."
                  )}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card className="h-100 text-center shadow-sm feature-card">
              <Card.Body>
                <FaBookOpen size={50} className="text-info mb-3" />
                <Card.Title className="fw-bold">
                  {t("feature_interactive_lessons", "Interactive Learning")}
                </Card.Title>
                <Card.Text>
                  {t(
                    "feature_interactive_lessons_desc",
                    "Keep students engaged with embedded quizzes, challenges, and downloadable materials directly within lessons."
                  )}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card className="h-100 text-center shadow-sm feature-card">
              <Card.Body>
                <FaChartLine size={50} className="text-warning mb-3" />
                <Card.Title className="fw-bold">
                  {t("feature_progress_tracking", "Progress Tracking")}
                </Card.Title>
                <Card.Text>
                  {t(
                    "feature_progress_tracking_desc",
                    "Monitor student progress with detailed statistics and visual charts for courses, modules, and quizzes."
                  )}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card className="h-100 text-center shadow-sm feature-card">
              <Card.Body>
                <FaComments size={50} className="text-danger mb-3" />
                <Card.Title className="fw-bold">
                  {t("feature_collaboration", "Collaboration Tools")}
                </Card.Title>
                <Card.Text>
                  {t(
                    "feature_collaboration_desc",
                    "Invite co-instructors to collaborate on course creation and management."
                  )}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card className="h-100 text-center shadow-sm feature-card">
              <Card.Body>
                <RiGitRepositoryPrivateFill
                  size={50}
                  className="text-secondary mb-3"
                />
                <Card.Title className="fw-bold">
                  {t("feature_privacy_control", "Privacy Control")}
                </Card.Title>
                <Card.Text>
                  {t(
                    "feature_privacy_control_desc",
                    "Choose whether your courses are public or private, controlling who can access your content."
                  )}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* How It Works Section */}
      <Container fluid className="bg-light py-5">
        <Container>
          <h2 className="text-center mb-5 fw-bold">
            {t("how_it_works", "How It Works")}
          </h2>
          <Row className="text-center">
            <Col md={4} className="mb-4 mb-md-0">
              <div className="p-4 bg-white rounded shadow-sm h-100">
                <div className="fs-1 text-primary mb-3">1</div>
                <h4 className="fw-bold">
                  {t("step_1_title", "Create or Join")}
                </h4>
                <p>
                  {t(
                    "step_1_desc",
                    "Teachers create engaging courses. Students join easily using a course code or by requesting access."
                  )}
                </p>
              </div>
            </Col>
            <Col md={4} className="mb-4 mb-md-0">
              <div className="p-4 bg-white rounded shadow-sm h-100">
                <div className="fs-1 text-primary mb-3">2</div>
                <h4 className="fw-bold">
                  {t("step_2_title", "Learn & Interact")}
                </h4>
                <p>
                  {t(
                    "step_2_desc",
                    "Access course materials, complete lessons, take quizzes, and participate in interactive challenges."
                  )}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="p-4 bg-white rounded shadow-sm h-100">
                <div className="fs-1 text-primary mb-3">3</div>
                <h4 className="fw-bold">
                  {t("step_3_title", "Track Progress")}
                </h4>
                <p>
                  {t(
                    "step_3_desc",
                    "Monitor learning progress through intuitive dashboards and detailed statistics provided for both students and teachers."
                  )}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </Container>

      {/* Bottom Call to Action */}
      <Container fluid className="bg-secondary text-white text-center py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <h2 className="fw-bold mb-3">
              {t("cta_title", "Ready to Transform Your Teaching and Learning?")}
            </h2>
            <p className="lead mb-4">
              {t(
                "cta_description",
                "Join thousands of educators and learners on ProfD today!"
              )}
            </p>
            {!authData.isLoggedIn ? (
              <Link to="/signup">
                <Button variant="primary" size="lg">
                  {t("signup_now", "Sign Up Now")}
                </Button>
              </Link>
            ) : (
              <Link to="/add-program">
                <Button variant="primary" size="lg">
                  {t("create_first_course", "Create Your First Course")}
                </Button>
              </Link>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}
